import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import type { ChipProps } from '@mui/material/Chip';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { AuditLogEntryDto } from '@/types/auditLog';

const SEVERITY_COLOR: Record<string, ChipProps['color']> = {
  Info: 'info',
  Warning: 'warning',
  Error: 'error',
};

type Row = AuditLogEntryDto & { id: string };

/**
 * SuperAdmin-only, cross-tenant activity log — every company's logins and actions in one list.
 * GET /audit-log/platform is deliberately a separate, more strictly-gated endpoint from the
 * ordinary GET /audit-log (company-scoped, for a future "my own company's audit trail" screen);
 * see AuditLogController for why the two are not the same call with a flag.
 */
export default function ActivityLogPage() {
  const { t } = useTranslation('activityLog');
  const { formatDateTime } = useDateFormat();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['audit-log', 'platform'],
    queryFn: async () => (await apiClient.get<AuditLogEntryDto[]>('/audit-log/platform')).data,
  });

  const rows: Row[] = entries.map((e) => ({ ...e, id: e.id }));

  const columns: DataTableColumn<Row>[] = [
    { field: 'createdDate', headerName: t('columns.date'), render: (r) => formatDateTime(r.createdDate) },
    {
      field: 'companyName',
      headerName: t('columns.company'),
      // Null reads as a genuinely platform-level action (a SuperAdmin's own login, or an entry
      // logged before this column existed) — labelled rather than left blank, so it doesn't look
      // like missing data.
      render: (r) => r.companyName ?? t('columns.platform'),
      filterable: true,
    },
    { field: 'userDisplayName', headerName: t('columns.user'), filterable: true },
    { field: 'module', headerName: t('columns.module'), filterable: true },
    { field: 'action', headerName: t('columns.action'), filterable: true },
    { field: 'details', headerName: t('columns.details') },
    {
      field: 'severity',
      headerName: t('columns.severity'),
      render: (r) => <Chip label={r.severity} color={SEVERITY_COLOR[r.severity] ?? 'default'} size="small" />,
      filterable: true,
    },
  ];

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />
      <DataTable columns={columns} rows={rows} loading={isLoading} searchable exportFileName="activity-log" />
    </>
  );
}
